import type { ComponentType } from 'react';
import { useEffect } from 'react';
import type { RouteRecord } from 'vite-react-ssg';
import { Outlet, ScrollRestoration, useRouteError } from 'react-router-dom';
import Nav from '@/components/Nav';
import Footer from '@/components/Footer';
import FragmentLink from '@/components/FragmentLink';
import OrganizationSchema from '@/components/OrganizationSchema';
import Home from '@/pages/Home';
import ContentPage from '@/pages/ContentPage';
import ToolPage from '@/pages/tools/ToolPage';
import { allContent, contentComponents } from '@/lib/content';
import { tools } from '@/tools/registry';

// Home is statically imported: it is the primary landing page and must hydrate
// without a lazy-load round-trip. Everything else uses `lazy` so each page is
// its own chunk. SSG still prerenders every route to its own HTML file.

const Layout = () => (
  <>
    <FragmentLink
      hash="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:bg-cream-light focus:px-4 focus:py-2 focus:text-small focus:text-forest"
    >
      Skip to content
    </FragmentLink>
    <OrganizationSchema />
    <Nav />
    <main id="main-content">
      <Outlet />
    </main>
    <Footer />
    {/* Native fragment navigation may reuse a history key. A URL-qualified key
        also keeps separate direct loads from sharing the initial "default" key. */}
    <ScrollRestoration getKey={(location) => `${location.key}:${location.pathname}${location.search}${location.hash}`} />
  </>
);

// Catches React Router errors — including a stale-chunk fetch after a deploy.
// Reload once (capped per session) to pull fresh HTML.
const RootErrorBoundary = () => {
  const error = useRouteError();
  useEffect(() => {
    const msg = (error as Error)?.message || String(error ?? '');
    const isStaleChunk =
      /is not valid JSON|Unexpected token '<'|Failed to fetch dynamically imported module|error loading dynamically imported module/.test(msg);
    if (isStaleChunk && typeof window !== 'undefined') {
      const KEY = 'ecmw-route-reloads';
      const n = Number(sessionStorage.getItem(KEY) || 0);
      if (n < 2) {
        sessionStorage.setItem(KEY, String(n + 1));
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <div className="container flex min-h-[60vh] items-center justify-center py-24 text-center">
      <div>
        <h1 className="text-subhead">Something went wrong</h1>
        <p className="mt-3">Reload the page to continue.</p>
        <button
          type="button"
          className="btn-forest mt-8"
          onClick={() => typeof window !== 'undefined' && window.location.reload()}
        >
          Reload
        </button>
      </div>
    </div>
  );
};

// After a deploy the open page may reference chunk filenames the new
// deployment no longer serves. Every route has its own prerendered HTML, so a
// full navigation to the target path completes the click with fresh assets.
const staleChunkNavigate = (path: string): Promise<never> | null => {
  if (typeof window === 'undefined') return null;
  const KEY = 'ecmw-chunk-nav';
  const n = Number(sessionStorage.getItem(KEY) || 0);
  if (n >= 3) return null;
  sessionStorage.setItem(KEY, String(n + 1));
  window.location.assign(path);
  return new Promise<never>(() => {});
};

const lazy = (load: () => Promise<{ default: ComponentType }>, path?: string) => async () => {
  try {
    const { default: Component } = await load();
    return { Component };
  } catch (err) {
    if (path) {
      const nav = staleChunkNavigate(path);
      if (nav) return nav;
    }
    throw err;
  }
};

export const routes: RouteRecord[] = [
  {
    path: '/',
    Component: Layout,
    ErrorBoundary: RootErrorBoundary,
    children: [
      { index: true, Component: Home, entry: 'src/pages/Home.tsx' },

      // Hubs (CONTENT-SPEC §1)
      { path: 'upgrades', lazy: lazy(() => import('@/pages/hubs/Upgrades'), '/upgrades'), entry: 'src/pages/hubs/Upgrades.tsx' },
      { path: 'migrations', lazy: lazy(() => import('@/pages/hubs/Migrations'), '/migrations'), entry: 'src/pages/hubs/Migrations.tsx' },
      { path: 'support', lazy: lazy(() => import('@/pages/hubs/Support'), '/support'), entry: 'src/pages/hubs/Support.tsx' },
      { path: 'sectors', lazy: lazy(() => import('@/pages/hubs/Sectors'), '/sectors'), entry: 'src/pages/hubs/Sectors.tsx' },
      { path: 'resources', lazy: lazy(() => import('@/pages/hubs/Resources'), '/resources'), entry: 'src/pages/hubs/Resources.tsx' },
      { path: 'notes', lazy: lazy(() => import('@/pages/hubs/Notes'), '/notes'), entry: 'src/pages/hubs/Notes.tsx' },
      { path: 'tools', lazy: lazy(() => import('@/pages/hubs/Tools'), '/tools'), entry: 'src/pages/hubs/Tools.tsx' },

      // One concrete route per MDX file so vite-react-ssg prerenders each to
      // its own HTML (route-level lazy is fully resolved during SSG).
      ...allContent.map((meta) => ({
        path: meta.path.slice(1),
        lazy: async () => {
          let Body: ComponentType;
          try {
            ({ default: Body } = await contentComponents[meta.path]());
          } catch (err) {
            const nav = staleChunkNavigate(meta.path);
            if (nav) return nav;
            throw err;
          }
          const Page = () => <ContentPage meta={meta} Body={Body} />;
          return { Component: Page };
        },
      })),

      // One route per registered tool (src/tools/registry.ts).
      ...tools.map((tool) => ({
        path: `tools/${tool.slug}`,
        lazy: async () => {
          let Tool: ComponentType;
          try {
            ({ default: Tool } = await tool.component());
          } catch (err) {
            const nav = staleChunkNavigate(`/tools/${tool.slug}`);
            if (nav) return nav;
            throw err;
          }
          const Page = () => <ToolPage tool={tool} Tool={Tool} />;
          return { Component: Page };
        },
      })),

      { path: 'estate-review', lazy: lazy(() => import('@/pages/EstateReview'), '/estate-review'), entry: 'src/pages/EstateReview.tsx' },
      { path: 'estate-review/sample', lazy: lazy(() => import('@/pages/EstateReviewSample'), '/estate-review/sample'), entry: 'src/pages/EstateReviewSample.tsx' },
      { path: 'about', lazy: lazy(() => import('@/pages/About'), '/about'), entry: 'src/pages/About.tsx' },
      { path: 'contact', lazy: lazy(() => import('@/pages/Contact'), '/contact'), entry: 'src/pages/Contact.tsx' },
      { path: 'privacy', lazy: lazy(() => import('@/pages/Privacy'), '/privacy'), entry: 'src/pages/Privacy.tsx' },
      { path: 'accessibility', lazy: lazy(() => import('@/pages/Accessibility'), '/accessibility'), entry: 'src/pages/Accessibility.tsx' },

      // Explicit /404 so vite-react-ssg prerenders dist/404.html. Cloudflare
      // Pages serves that file (status 404) for any unknown URL.
      { path: '404', lazy: lazy(() => import('@/pages/NotFound'), '/404'), entry: 'src/pages/NotFound.tsx' },
      { path: '*', lazy: lazy(() => import('@/pages/NotFound')) },
    ],
  },
];
